import { Injectable } from '@angular/core';

/**
 * Registry of the setting cards currently rendered on the page, keyed by
 * anchor id, so a deep link (`/settings/<tab>#<id>`) can scroll to and
 * highlight a card even when its tab is lazy-loaded and not rendered yet:
 * a reveal requested before the card registers is kept pending and replayed
 * as soon as the matching `appSettingAnchor` shows up.
 */
@Injectable({ providedIn: 'root' })
export class SettingsAnchorService {
  public static readonly TARGETED_CLASS = 'v-anchor--targeted';
  public static readonly HIGHLIGHT_DURATION_MS = 3000;
  /** How long to wait for a freshly rendered tab (editors, images, async data) to stop shifting before re-checking. */
  public static readonly SETTLE_MS = 600;

  private readonly anchors = new Map<string, HTMLElement>();
  private pendingId: string | null = null;
  private highlightTimer: ReturnType<typeof setTimeout> | null = null;
  private deferredTimers: ReturnType<typeof setTimeout>[] = [];
  private highlighted: HTMLElement | null = null;

  public register(id: string, element: HTMLElement): void {
    if (!id) {
      return;
    }

    this.anchors.set(id, element);

    if (this.pendingId === id) {
      this.pendingId = null;
      this.revealWhenSettled(id);
    }
  }

  public unregister(id: string, element: HTMLElement): void {
    if (this.anchors.get(id) === element) {
      this.anchors.delete(id);
    }
  }

  public has(id: string): boolean {
    return this.anchors.has(id);
  }

  public get pending(): string | null {
    return this.pendingId;
  }

  public cancelPending(): void {
    this.pendingId = null;
    this.clearDeferred();
  }

  /**
   * Scrolls the card into view, moves focus to it and flashes a highlight.
   * Returns false (and remembers the id) when the card is not rendered yet.
   */
  public reveal(id: string, behavior: ScrollBehavior = 'smooth'): boolean {
    const element = this.anchors.get(id);

    if (!element) {
      this.pendingId = id;
      return false;
    }

    this.pendingId = null;
    this.clearDeferred();
    this.clearHighlight();

    element.scrollIntoView({ block: 'center', behavior });
    element.focus({ preventScroll: true });

    element.classList.add(SettingsAnchorService.TARGETED_CLASS);
    this.highlighted = element;
    this.highlightTimer = setTimeout(
      () => this.clearHighlight(),
      SettingsAnchorService.HIGHLIGHT_DURATION_MS
    );

    return true;
  }

  /**
   * A card that registers while its tab is still rendering would be scrolled
   * to before the cards above it exist, leaving it off-screen once they do.
   * Scroll after the current render, then once more if the layout drifted.
   */
  private revealWhenSettled(id: string): void {
    this.clearDeferred();

    this.deferredTimers.push(
      setTimeout(() => {
        this.reveal(id, 'auto');

        this.deferredTimers.push(
          setTimeout(
            () => this.ensureVisible(id),
            SettingsAnchorService.SETTLE_MS
          )
        );
      })
    );
  }

  private ensureVisible(id: string): void {
    const element = this.anchors.get(id);

    if (!element) {
      return;
    }

    const rect = element.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;

    if (rect.top < 0 || rect.bottom > viewportHeight) {
      element.scrollIntoView({ block: 'center', behavior: 'auto' });
    }
  }

  private clearDeferred(): void {
    this.deferredTimers.forEach((timer) => clearTimeout(timer));
    this.deferredTimers = [];
  }

  private clearHighlight(): void {
    if (this.highlightTimer) {
      clearTimeout(this.highlightTimer);
      this.highlightTimer = null;
    }

    this.highlighted?.classList.remove(SettingsAnchorService.TARGETED_CLASS);
    this.highlighted = null;
  }
}
